import React from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SeverityAssessmentStep = ({ formData, onChange, errors }) => {
  const urgencyLevels = [
    {
      value: 'immediate',
      label: 'Immediate (within 24 hours)',
      description: 'I need support as soon as possible',
      icon: 'AlertCircle',
      color: 'text-error'
    },
    {
      value: 'urgent',
      label: 'Urgent (within 2-3 days)',
      description: 'I need support soon but can wait a bit',
      icon: 'Clock',
      color: 'text-warning'
    },
    {
      value: 'routine',
      label: 'Routine (within 1-2 weeks)',
      description: 'I can wait for the right match',
      icon: 'Calendar',
      color: 'text-primary'
    },
    {
      value: 'flexible',
      label: 'Flexible (no specific timeline)',
      description: 'I\'m exploring options for future support',
      icon: 'CalendarDays',
      color: 'text-success'
    }
  ];

  const impactOptions = [
    { value: 'minimal', label: 'Minimal - Minor disruption to daily life' },
    { value: 'moderate', label: 'Moderate - Some difficulty with daily activities' },
    { value: 'significant', label: 'Significant - Major impact on work/relationships' },
    { value: 'severe', label: 'Severe - Unable to function normally' }
  ];

  const handleUrgencySelect = (value) => {
    onChange('urgencyLevel', value);
    
    // Auto-expand search radius for acute cases
    if (value === 'immediate' || value === 'urgent') {
      onChange('expandSearchRadius', true);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Urgency & Impact Assessment
        </h3>
        <p className="text-sm text-muted-foreground">
          Help us prioritize your needs and find appropriate support
        </p>
      </div>
      <div className="space-y-3">
        <label className="text-sm font-medium text-foreground">
          How soon do you need support? <span className="text-error">*</span>
        </label>
        {urgencyLevels?.map((level) => (
          <div
            key={level?.value}
            onClick={() => handleUrgencySelect(level?.value)}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              formData?.urgencyLevel === level?.value
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 bg-card'
            }`}
          >
            <div className="flex items-start gap-3">
              <Icon name={level?.icon} size={24} className={level?.color} />
              <div className="flex-1">
                <p className="font-medium text-foreground mb-1">{level?.label}</p>
                <p className="text-sm text-muted-foreground">{level?.description}</p>
              </div>
              {formData?.urgencyLevel === level?.value && (
                <Icon name="CheckCircle2" size={20} className="text-primary" />
              )}
            </div>
          </div>
        ))}
        {errors?.urgencyLevel && (
          <p className="text-sm text-error">{errors?.urgencyLevel}</p>
        )}
      </div>
      <Select
        label="Impact on daily functioning"
        placeholder="Select impact level"
        options={impactOptions}
        value={formData?.impactLevel || ''}
        onChange={(value) => onChange('impactLevel', value)}
        description="How much are these issues affecting your daily life?"
        required
        error={errors?.impactLevel}
      />
      <div className="space-y-3">
        <label className="text-sm font-medium text-foreground">
          Safety Screening
        </label>
        <Checkbox
          label="I am experiencing thoughts of self-harm or suicide"
          checked={formData?.selfHarmThoughts || false}
          onChange={(e) => onChange('selfHarmThoughts', e?.target?.checked)}
          description="Checking this will prioritize crisis resources"
        />
        <Checkbox
          label="I am concerned about harming others"
          checked={formData?.harmToOthers || false}
          onChange={(e) => onChange('harmToOthers', e?.target?.checked)}
          description="Your safety and others' safety is our priority"
        />
      </div>
      {(formData?.urgencyLevel === 'immediate' || formData?.urgencyLevel === 'urgent') && (
        <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary mt-0.5" />
            <div className="text-sm">
              <p className="text-foreground font-medium mb-1">Expanded Search Enabled</p>
              <p className="text-muted-foreground">
                Due to your urgency level, we'll automatically search across all locations and expand helper availability to find you support faster.
              </p>
            </div>
          </div>
        </div>
      )}
      {(formData?.selfHarmThoughts || formData?.harmToOthers) && (
        <div className="p-4 bg-error/10 rounded-lg border border-error/20">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} className="text-error mt-0.5" />
            <div className="text-sm">
              <p className="text-foreground font-medium mb-2">Immediate Crisis Resources</p>
              <div className="space-y-2">
                <p className="text-muted-foreground">
                  <strong>National Suicide & Crisis Lifeline:</strong> Call or text XXX (available 24/7)
                </p>
                <p className="text-muted-foreground">
                  <strong>Crisis Text Line:</strong> Text HOME to XXXXX
                </p>
                <p className="text-muted-foreground">
                  <strong>Emergency Services:</strong> Call XXX or visit your nearest emergency room
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeverityAssessmentStep;